import { useEffect, useState } from 'react'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { putFetcher } from '@/hooks'
import { useToast } from '@/hooks/use-toast'
import { useRole } from '@/hooks/auth'
import { Role } from '@/constants/role'

const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(value)

/** bot 资料编辑：名称 + 图标 URL（真人的资料来自飞书，不可改） */
const BotProfileDialog = ({ bot }) => {
  const isAdmin = useRole(Role.Admin, true)
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [avatar, setAvatar] = useState('')
  const { trigger, isMutating, error } = useSWRMutation(
    `/users/${bot?.id}`,
    putFetcher,
  )

  useEffect(() => {
    if (open) {
      setName(bot?.name ?? '')
      setAvatar(bot?.avatarThumb ?? '')
    }
  }, [open, bot])

  if (!isAdmin || bot?.kind !== 'bot') return null

  const trimmedName = name.trim()
  const trimmedAvatar = avatar.trim()
  const avatarValid = trimmedAvatar === '' || isHttpUrl(trimmedAvatar)
  const changed =
    trimmedName !== bot.name || trimmedAvatar !== (bot.avatarThumb ?? '')
  const canSave = trimmedName && avatarValid && changed && !isMutating

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSave) return
    await trigger({ name: trimmedName, avatarThumb: trimmedAvatar })
    mutate([`/users/${bot.id}/full`])
    mutate(['/users'])
    toast({ title: '已保存 bot 资料' })
    setOpen(false)
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil className="mr-2 h-4 w-4" />
        编辑资料
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑 Bot 资料</DialogTitle>
            <DialogDescription>
              图标填一个可公开访问的图片地址（http/https），留空则显示默认占位。
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bot-name">名称</Label>
              <Input
                id="bot-name"
                value={name}
                maxLength={255}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bot-avatar">图标 URL</Label>
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 shrink-0">
                  <AvatarImage src={avatarValid ? trimmedAvatar : ''} alt="" />
                  <AvatarFallback>BOT</AvatarFallback>
                </Avatar>
                <Input
                  id="bot-avatar"
                  placeholder="https://…/icon.png"
                  value={avatar}
                  maxLength={255}
                  onChange={(e) => setAvatar(e.target.value)}
                />
              </div>
              {!avatarValid && (
                <p className="text-xs text-destructive">
                  必须是以 http:// 或 https:// 开头的地址
                </p>
              )}
            </div>
            {error && (
              <p className="text-sm text-destructive">{error.message}</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                取消
              </Button>
              <Button type="submit" disabled={!canSave}>
                {isMutating ? '保存中…' : '保存'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BotProfileDialog
